export interface NormalizedPdfBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One address inside the Document Workspace. Pages are one-based at this viewer-facing seam. */
export interface DocumentWorkspaceTarget {
  documentId?: string;
  pageNumber?: number;
  highlight?: NormalizedPdfBox;
}

export const DOCUMENT_WORKSPACE_TARGET_PARAMS = {
  documentId: "documentId",
  page: "page",
  highlight: "highlight",
} as const;

const validBox = (box: NormalizedPdfBox): boolean =>
  [box.x, box.y, box.width, box.height].every(Number.isFinite) &&
  box.x >= 0 &&
  box.y >= 0 &&
  box.width > 0 &&
  box.height > 0 &&
  box.x + box.width <= 1.000_001 &&
  box.y + box.height <= 1.000_001;

/** Encode a workspace address using the URL representation used by every caller. */
export function documentWorkspaceTargetSearchParams(
  target: DocumentWorkspaceTarget,
  base?: URLSearchParams
): URLSearchParams {
  const params = new URLSearchParams(base);
  for (const name of Object.values(DOCUMENT_WORKSPACE_TARGET_PARAMS)) params.delete(name);
  if (target.documentId) params.set(DOCUMENT_WORKSPACE_TARGET_PARAMS.documentId, target.documentId);
  const hasPage = !!target.pageNumber && Number.isInteger(target.pageNumber) && target.pageNumber > 0;
  if (hasPage) {
    params.set(DOCUMENT_WORKSPACE_TARGET_PARAMS.page, String(target.pageNumber));
  }
  if (hasPage && target.highlight && validBox(target.highlight)) {
    params.set(
      DOCUMENT_WORKSPACE_TARGET_PARAMS.highlight,
      [target.highlight.x, target.highlight.y, target.highlight.width, target.highlight.height].join(",")
    );
  }
  return params;
}

/** Decode the canonical URL params. Invalid or incomplete highlights are ignored safely. */
export function documentWorkspaceTargetFromSearchParams(
  input: URLSearchParams | string
): DocumentWorkspaceTarget {
  const params = typeof input === "string" ? new URLSearchParams(input) : input;
  const documentId = params.get(DOCUMENT_WORKSPACE_TARGET_PARAMS.documentId)?.trim() || undefined;
  const rawPage = Number(params.get(DOCUMENT_WORKSPACE_TARGET_PARAMS.page));
  const pageNumber = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : undefined;
  const values = params.get(DOCUMENT_WORKSPACE_TARGET_PARAMS.highlight)?.split(",").map(Number);
  const candidate =
    values?.length === 4
      ? { x: values[0]!, y: values[1]!, width: values[2]!, height: values[3]! }
      : undefined;
  return {
    ...(documentId ? { documentId } : {}),
    ...(pageNumber ? { pageNumber } : {}),
    ...(pageNumber && candidate && validBox(candidate) ? { highlight: candidate } : {}),
  };
}

/** Rotate a normalized, top-left-origin rectangle with the rendered PDF page. */
export function rotateNormalizedPdfBox(box: NormalizedPdfBox, rotation: number): NormalizedPdfBox {
  const normalized = ((rotation % 360) + 360) % 360;
  if (normalized === 90) {
    return { x: 1 - box.y - box.height, y: box.x, width: box.height, height: box.width };
  }
  if (normalized === 180) {
    return { x: 1 - box.x - box.width, y: 1 - box.y - box.height, width: box.width, height: box.height };
  }
  if (normalized === 270) {
    return { x: box.y, y: 1 - box.x - box.width, width: box.height, height: box.width };
  }
  return box;
}
